import { useCallback, useEffect, useRef, useState } from "react";
import type { TFunction } from "react-i18next";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import {
  CheckBoxContainer,
  CheckBoxFinePrintLabel,
  SectionTitle,
} from "../../../../../../components/Form/Form";
import { Form } from "../../../../../../layout/FormLayout";
import {
  ButtonContainer,
  PrimaryButtonMediumV2,
  TertiaryButtonMediumV2,
} from "../../../../../../components/Buttons/Buttons";
import { z } from "zod";
import { useNotifications } from "../../../../../../components/Notifications/NotificationsContext";
import { useFormWrapper, useStoreState } from "../../../../../../util/util";
import { FilePickerUncontrolled } from "../../../../../../components/FilePicker/FilePickerUncontrolled";
import type {
  UploadAssetsResponseSchema,
  ProductUploadStatus,
  UploadedAssetSchema,
} from "../../../../../../types/types.PIM";
import { zodResolver } from "@hookform/resolvers/zod";
import { MAXIMUM_FILE_SIZE } from "../../../../../../components/FilePicker/FilePicker.constants";
import { endpoints } from "../../../../../../endpoints";
import { HelpDialog } from "../../../../../SharedPages/OrganizationPage/ProductsList/CreatePimProductPage/CreateFromUploads/HelpDialog";
import {
  H6Bold,
  SmallSectionHeaderRegular,
} from "../../../../../../components/Typography/Typography";
import {
  DownloadIcon,
  InfoIcon,
  LoadingIcon,
} from "../../../../../../components/Icons/Icons";
import { UploadPopups } from "../../../../../SharedPages/OrganizationPage/ProductsList/CreatePimProductPage/CreateFromUploads/UploadPopups";
import type { AxiosError } from "axios";
import axios from "axios";
import { StringParam, useQueryParams } from "use-query-params";
import { CheckBoxNoLabel } from "../../../../../../components/CheckBoxes/CheckBoxes";
import { useHistory } from "react-router-dom";
import { useRoutePath } from "../../../../../../util/Routing";
import { PreviousExternalAssetsUploads } from "./PreviousExternalAssetsUploads";
import { spacingTokens } from "../../../../../../designTokens";

const AddFilesContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`;

const GuideSection = styled.section`
  position: absolute;
  right: 0;
  top: 89px;
`;

const GuideContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const AddExternalAssetSchema = z.object({
  file: z.instanceof(File),
  replace_existing: z.boolean().default(false).optional(),
});

const guidelineOverview = (t: TFunction) => [
  t("Download the CSV template by clicking the 'Download Template' link."),
  t(
    "Fill in the template with your externally hosted asset details — each row represents one asset. Each row can have a different Asset Type, Category, and Asset Host."
  ),
  t(
    "To associate assets with products, add the Product IDs in the Product ID column."
  ),
  t(
    "To update existing externally hosted assets, check the 'Include existing asset data' checkbox before downloading the template. The CSV will be prefilled with your existing externally hosted assets. Modify the rows you want to update and add new rows for new assets."
  ),
  t("Upload the completed CSV file using the upload box."),
  t(
    "The system will validate and process your CSV. If any rows fail validation, the entire upload will fail and you can download an error log from the Previous Uploads table to see which rows had issues."
  ),
  t(
    "If the upload is successful, you can download a success log from the Previous Uploads table to see which assets were created or updated."
  ),
  t(
    "Checking the 'Replace existing assets' box will replace any existing externally hosted assets that have the same URL as a new asset in your CSV. If this is unchecked, new rows with duplicate URLs will be skipped."
  ),
];

const csvGuidelines = (t: TFunction) => [
  t("Use the provided CSV template — do not modify the column headers."),
  t(
    "Asset Name, URL, Asset Type, Category, and Asset Host are required for each row."
  ),
  t(
    "Each URL must be a valid, reachable link to the externally hosted document."
  ),
  t(
    "Asset Type, Category, Asset Host, Document Type, Country, and Region values must match existing values in the system."
  ),
  t("Product IDs must match existing products in the system."),
  t(
    "Do not modify the ID (read-only) column — this is used by the system to identify existing assets for updates."
  ),
  t("Dates must be in MM-DD-YYYY format."),
  t("The CSV file must be under 50 MB in size."),
];

type FormValues = z.infer<typeof AddExternalAssetSchema>;

const AddExternalAssetSchemaFn = (t: (s: string) => string) =>
  z
    .object({
      file: z.instanceof(File),
      replace_existing: z.boolean().default(false).optional(),
    })
    .superRefine(({ file }, ctx) => {
      if (file.size > MAXIMUM_FILE_SIZE) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${file.name} ${t("is above the maximum file size")}`,
          path: ["file"],
        });
      }
    });

export const AddExternalAssetFiles = () => {
  const { t } = useTranslation();
  const { notifyError, notifySuccess } = useNotifications();
  const { storefront_id, tenant_id } = useStoreState();

  const [showDialog, setShowDialog] = useState(false);
  const [csvContent, setCsvContent] = useState<string | null>(null);
  const [showGuide, setShowGuide] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [query, setQuery] = useQueryParams({
    extUploadJobId: StringParam,
    extUploadNumber: StringParam,
  });
  const [jobId, setJobId] = useState<string | undefined>(
    query?.extUploadJobId ?? undefined
  );
  const [uploadNumber, setUploadNumber] = useState<string | undefined>(
    query?.extUploadNumber ?? undefined
  );
  const [includeExistingData, setIncludeExistingData] = useState(false);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [isGeneratingCSV, setIsGeneratingCSV] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<ProductUploadStatus>("paused");
  const [list_mutate_trigger, setListMutateTrigger] = useState<string>();
  const { adminPath } = useRoutePath();
  const csvIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const theme = useTheme();
  const history = useHistory();
  const uploadResponseIntervalRef =
    useRef<ReturnType<typeof setInterval> | null>(null);
  const methodsOfUseForm = useFormWrapper({
    resolver: zodResolver(AddExternalAssetSchemaFn(t)),
    defaultValues: {
      replace_existing: false,
      file: undefined,
    },
  });

  const { handleSubmit, setValue, register } = methodsOfUseForm;

  const stopPollingForCSV = () => {
    if (csvIntervalRef.current) {
      clearInterval(csvIntervalRef.current);
      csvIntervalRef.current = null;
    }
  };

  const startPollingForCSV = (job_id: string) => {
    stopPollingForCSV();
    csvIntervalRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get<
          string | { status: "processing" | "failed" }
        >(
          endpoints.v2_storefronts_storefront_id_pim_assets_external_download(
            storefront_id
          ) + `?job_id=${job_id}`
        );
        if (typeof data === "string") {
          setCsvContent(data);
          setIsGeneratingCSV(false);
          stopPollingForCSV();
        } else if (data.status === "failed") {
          notifyError(
            t(
              "There was an error generating your CSV template. Please try again."
            )
          );
          setIsGeneratingCSV(false);
          stopPollingForCSV();
        }
      } catch (error) {
        notifyError(
          t(
            "There was an error generating your CSV template. Please try again."
          ),
          {
            error,
          }
        );
        setIsGeneratingCSV(false);
        stopPollingForCSV();
      }
    }, 1500);
  };

  const handleGenerateCSV = async () => {
    try {
      setIsGeneratingCSV(true);
      const { data } = await axios.post<{ job_id: string }>(
        endpoints.v2_storefronts_storefront_id_pim_assets_external_download(
          storefront_id
        ),
        {
          scope: includeExistingData ? "existing_data" : "empty_csv",
        }
      );
      startPollingForCSV(data.job_id);
    } catch (error) {
      notifyError(
        t("There was an error generating your CSV template. Please try again."),
        {
          error,
        }
      );
      setIsGeneratingCSV(false);
    }
  };

  const handleDownloadCSV = () => {
    if (csvContent) {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(
        new Blob([csvContent], { type: "text/csv" })
      );
      link.download = "pim_assets_upload_template.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }
  };

  const stopPollingForUploadResponse = () => {
    if (uploadResponseIntervalRef.current) {
      clearInterval(uploadResponseIntervalRef.current);
      uploadResponseIntervalRef.current = null;
    }
  };

  const handleUploadComplete = useCallback(() => {
    stopPollingForUploadResponse();
    setShowDialog(false);
    setStatus("successful");
    setListMutateTrigger(`success-${Date.now()}`);
    setCsvContent(null);
    notifySuccess(t("Your files have been uploaded successfully."));
    requestAnimationFrame(() => {
      const parent = document.getElementById(
        "bulkupload_container"
      )?.parentElement;
      if (parent) {
        parent.scrollTo({ top: parent.scrollHeight, behavior: "smooth" });
      }
    });
  }, [notifySuccess, t]);

  const startPollingForUploadResponse = useCallback(
    (upload_id: string) => {
      stopPollingForUploadResponse();
      uploadResponseIntervalRef.current = setInterval(async () => {
        try {
          const {
            data: { upload_status, error_details },
          } = await axios.get<UploadedAssetSchema>(
            endpoints.v2_tenants_tenant_id_pim_assets_uploads_upload_id(
              tenant_id,
              upload_id
            )
          );
          if (upload_status === "completed") {
            handleUploadComplete();
          } else if (upload_status === "failed") {
            setStatus("failed");
            notifyError(
              error_details ??
                t(
                  "There was an error processing your uploaded files. Please try again."
                )
            );
            stopPollingForUploadResponse();
            setShowDialog(false);
          }
        } catch (error) {
          const errorMessage = (error as AxiosError)?.response?.data?.message;
          notifyError(
            errorMessage
              ? errorMessage
              : t(
                  "There was an error processing your uploaded files. Please try again."
                ),
            { error }
          );
          stopPollingForUploadResponse();
          setShowDialog(false);
          setStatus("failed");
        }
      }, 1500);
    },
    [handleUploadComplete, notifyError, tenant_id, t]
  );

  const onCompleteUpload = () => {
    setQuery({
      extUploadNumber: undefined,
      extUploadJobId: undefined,
    });
    setJobId(undefined);
    setUploadNumber(undefined);
    history.push(`${adminPath}/pim/assets`);
  };

  const onSubmit = async (values: FormValues) => {
    setStatus("in_progress");
    setDialogMessage(
      t("Your files are currently being uploaded in the background.")
    );
    setShowDialog(true);
    setIsSubmitting(true);
    try {
      const { file, replace_existing } = values;
      const formData = new FormData();
      formData.append("file", file);
      if (replace_existing !== undefined) {
        formData.append("replace_existing", String(replace_existing));
      }
      const {
        data: {
          asset_upload: { number, id: upload_id },
          job_id,
        },
      } = await axios.post<{
        asset_upload: UploadAssetsResponseSchema;
        job_id: string;
      }>(
        endpoints.v2_storefronts_storefront_id_pim_assets_external_upload(
          storefront_id
        ),
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      setJobId(job_id);
      setUploadNumber(number);
      setQuery({
        extUploadJobId: job_id,
        extUploadNumber: number,
      });
      startPollingForUploadResponse(upload_id);
      setValue("file", undefined);
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("There was an error uploading your files."),
        {
          error,
        }
      );
      setShowDialog(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (query?.extUploadJobId && query?.extUploadNumber && !jobId) {
      setJobId(query.extUploadJobId);
      setUploadNumber(query.extUploadNumber);
      setShowDialog(true);
      setStatus("in_progress");
      startPollingForUploadResponse(query.extUploadJobId);
    }
  }, [
    jobId,
    query.extUploadJobId,
    query.extUploadNumber,
    startPollingForUploadResponse,
  ]);

  useEffect(() => {
    register("replace_existing");
  }, [register]);

  useEffect(() => {
    return () => {
      stopPollingForCSV();
      stopPollingForUploadResponse();
    };
  }, []);

  return (
    <AddFilesContainer>
      <SectionTitle
        style={{
          marginTop: theme.resolve("spacing_3xl"),
          marginBottom: theme.resolve("spacing_lg"),
        }}
      >
        {t("Add files below")}
      </SectionTitle>
      <Form
        style={{
          maxWidth: "512px",
        }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <CheckBoxContainer>
          <div style={{ minWidth: "12px" }}>
            <CheckBoxNoLabel
              id="include_existing_data"
              style={{ marginRight: "5px" }}
              onChange={(e) => {
                setIncludeExistingData(e.target.checked);
              }}
              checked={includeExistingData}
              disabled={!!csvContent || status === "in_progress"}
            />
          </div>
          <CheckBoxFinePrintLabel
            style={{
              cursor:
                !!csvContent || status === "in_progress"
                  ? "not-allowed"
                  : "pointer",
            }}
            htmlFor="include_existing_data"
          >
            {t("Include Existing Data")}
          </CheckBoxFinePrintLabel>
        </CheckBoxContainer>
        {!csvContent ? (
          <PrimaryButtonMediumV2
            onClick={handleGenerateCSV}
            disabled={isGeneratingCSV}
          >
            {isGeneratingCSV && (
              <span style={{ paddingRight: spacingTokens.spacing_md }}>
                <LoadingIcon width={14} height={14} />
              </span>
            )}
            {isGeneratingCSV ? t("Processing...") : t("Download")}
          </PrimaryButtonMediumV2>
        ) : (
          <TertiaryButtonMediumV2 type="button" onClick={handleDownloadCSV}>
            <DownloadIcon width={20} height={20} />
            {t("Download CSV Template")}
          </TertiaryButtonMediumV2>
        )}
        <FilePickerUncontrolled
          methodsOfUseForm={methodsOfUseForm}
          placeHolderText={t("Drag and drop files, OR")}
          name="file"
          accept={[".csv"]}
          description={t("Max file size: 50 mb / file")}
          required={true}
        />
        <CheckBoxContainer>
          <div style={{ minWidth: "12px" }}>
            <CheckBoxNoLabel
              id="replace_existing"
              style={{ marginRight: "5px" }}
              onChange={(e) => {
                setReplaceExisting(e.target.checked);
                setValue("replace_existing", e.target.checked);
              }}
              disabled={status === "in_progress"}
              checked={replaceExisting}
            />
          </div>
          <CheckBoxFinePrintLabel
            style={{
              cursor: status === "in_progress" ? "not-allowed" : "pointer",
            }}
            htmlFor="replace_existing"
          >
            {t("Replace existing files")}
          </CheckBoxFinePrintLabel>
        </CheckBoxContainer>
        <ButtonContainer>
          <TertiaryButtonMediumV2
            disabled={isSubmitting}
            type="button"
            onClick={onCompleteUpload}
          >
            {t("Back")}
          </TertiaryButtonMediumV2>
          <PrimaryButtonMediumV2
            type="submit"
            loading={isSubmitting}
            disabled={status === "in_progress"}
          >
            {t("Upload")}
          </PrimaryButtonMediumV2>
        </ButtonContainer>
      </Form>
      <PreviousExternalAssetsUploads triggerMutate={list_mutate_trigger} />
      <GuideSection>
        <GuideContainer
          style={{ alignSelf: "flex-start", gap: spacingTokens.spacing_xs }}
        >
          <HelpDialog
            show={showGuide}
            setShow={setShowGuide}
            overview={guidelineOverview}
            guidelines={csvGuidelines}
            guidelineHeader={t("Guidelines for Updating Your Files")}
          />
          <SmallSectionHeaderRegular style={{ margin: 0 }}>
            {t("Need help getting started?")}
          </SmallSectionHeaderRegular>
          <div
            onClick={() => setShowGuide(true)}
            style={{
              display: "flex",
              gap: spacingTokens.spacing_xs,
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <InfoIcon />
            <H6Bold>{t("View Guide")}</H6Bold>
          </div>
        </GuideContainer>
      </GuideSection>
      <UploadPopups
        showDialog={showDialog}
        setShowDialog={setShowDialog}
        onComplete={() => setShowDialog(false)}
        message={dialogMessage}
        status={status}
        uploadId={uploadNumber ?? ""}
        hideCloseBtn={status === "in_progress" ? false : true}
      />
    </AddFilesContainer>
  );
};
